import get from 'lodash/get';
import gt from 'lodash/gt';
import chain from 'lodash/chain';
import identity from 'lodash/identity';
import negate from 'lodash/negate';
import includes from 'lodash/includes';
import find from 'lodash/find';
import curry from 'lodash/curry';
import isBoolean from 'lodash/isBoolean';
import inRange from 'lodash/inRange';

import has from 'lodash/fp/has';

// eslint-disable-next-line no-magic-numbers
const excludes = curry(negate(includes), 2);

const escapeEl = document.createElement('textarea');
const textEl = document.createElement('span');

const ANIMATION_DURATION = 250;

export function classList(el) {
   if (get(el, 'classList')) {
      return el.classList;
   }

   return {
      add(...classes) {
         addClasses(el, classes);
      },
      remove(...classes) {
         removeClasses(el, classes);
      },
      toggle(className, flag) {
         if (isBoolean(flag)) {
            return flag ? addClasses(el, [className]) : removeClasses(el, [className]);
         }

         return toggleClasses(el, [className]);
      },
      contains(className) {
         return containsClass(el, className);
      }
   };
}

export function addClasses(el, classes) {
   const chain = _getClassesAsChain(el).union(classes).uniq();
   el.setAttribute('class', chain.join(' ').value());
}

export function removeClasses(el, classes) {
   const chain = _getClassesAsChain(el).filter(excludes(classes));
   el.setAttribute('class', chain.join(' ').value());
}

export function toggleClasses(el, classes) {
   const chain = _getClassesAsChain(el).xor(classes);
   el.setAttribute('class', chain.join(' ').value());
}

export function containsClass(el, className) {
   return _getClassesAsChain(el).includes(className).value();
}

export function closest(node, selector) {
   if (node.closest) {
      return node.closest(selector);
   }

   while (node) {
      if (matches(node, selector)) {
         return node;
      }
      node = node.parentElement;
   }
   return null;
}

function matches(node, selector) {
   if (node.matches) {
      return node.matches(selector);
   }

   if (node.webkitMatchesSelector) {
      return node.webkitMatchesSelector(selector);
   }

   var elements = (node.document || node.ownerDocument).querySelectorAll(selector);
   var index = 0;

   while (elements[index] && elements[index] !== node) {
      ++index;
   }

   return Boolean(elements[index]);
}

function _getClassesAsChain(el) {
   return chain(el.getAttribute('class')).split(' ').filter(identity);
}

export function escapeHTML(html) {
   escapeEl.textContent = html;
   return escapeEl.innerHTML;
}

export function textContent(html) {
   textEl.innerHTML = html;
   return textEl.textContent;
}

export function scrollIntoViewIfNeeded(el, useAnimation = false, duration = ANIMATION_DURATION) {
   let parent = _getScrollableParent(el);
   if (!parent) {
      return;
   }

   const { top, bottom } = el.getBoundingClientRect();
   const rect = parent.getBoundingClientRect();

   let visible = true;
   // eslint-disable-next-line no-magic-numbers
   visible = visible && gt(top, 0);
   visible = visible && inRange(top, rect.top, rect.bottom);
   visible = visible && inRange(bottom, rect.top, rect.bottom);

   if (visible) {
      return;
   }

   const startPosition = parent.scrollTop;
   const delta = Math.floor(top - rect.top);
   const headerTop = 64;
   const destination = parent === _mainScrollingElement() ? delta - headerTop : startPosition + delta;
   if (useAnimation) {
      _animateMe(function _scrollStep(easing) {
         _scrollTo(parent, startPosition + (destination - startPosition) * easing);
      }, duration);
   } else {
      _scrollTo(parent, destination);
   }
}

function _getScrollableParent(node) {
   const body = document.body;
   while (node) {
      if (_hasScroll(node)) {
         return node === body || node.contains(body) ? _mainScrollingElement() : node;
      }
      node = node.parentElement;
   }

   return null;
}

function _mainScrollingElement() {
   return document.scrollingElement || document.documentElement;
}

function _scrollTo(el, top) {
   el.scrollTop = top;
}

function _animateMe(callback, duration = ANIMATION_DURATION, easingFn = _quadratic) {
   const startTime = _now();
   const destTime = startTime + duration;

   function _step() {
      const now = _now();

      if ( now >= destTime ) {
         const lastStep = 1;
         callback(lastStep);
         return;
      }

      const easing = easingFn(( now - startTime ) / duration);
      callback(easing);
      rAF(_step);
   }

   _step();
}

function _hasScroll(el) {
   return el.scrollHeight > (el.clientHeight || el.offsetHeight);
}

function _now() {
   return performance && performance.now ? performance.now() : Date.now();
}

function _quadratic(k) {
   // eslint-disable-next-line no-magic-numbers
   return k * ( 2 - k );
}

const RAF_KEY = (function _rafKeyDetector() {
   const keys = [
      'requestAnimationFrame',
      'msRequestAnimationFrame',
      'webkitRequestAnimationFrame',
      'mozRequestAnimationFrame',
      'oRequestAnimationFrame'
   ];

   return find(keys, has(window));
})();

export function rAF(callback) {
   if (RAF_KEY) {
      window[RAF_KEY](callback);
   } else {
      // eslint-disable-next-line no-magic-numbers
      setTimeout(callback, 1000 / 60);
   }
}

const ZERO = 0;
export function scrollTop() {
   const doc = document.documentElement;
   return (window.pageYOffset || doc.scrollTop)  - (doc.clientTop || ZERO);
}
